/**
 * The hub's own SEO checks: non-blocking hints (counters, inline warnings, the Add route dialog's refusals). The
 * server's `issues` are authoritative and the only thing that disables Publish (Decision 13): see {@link blocksPublish}.
 */
import { derivedWildcard, footerRulePathOf, isPatternPath } from './route-path';
import type { ImageRef, Localized, SiteSeoDocument, SiteSeoIssue, SiteSeoPage } from './seo-types';
import { NAME_TOKEN, templateHasPageToken } from './title-template';

export type SeoHintCode =
  | 'title-long'
  | 'description-short'
  | 'description-long'
  | 'template-no-page'
  | 'canonical-invalid'
  | 'image-small'
  | 'default-language-missing'
  | 'placeholder'
  | 'llms-summary-long'
  | 'llms-facts-long'
  | 'name-token-unsupported'
  | 'wildcard-collision'
  | 'meta-markup'
  | 'image-url-invalid'
  | 'footer-path-duplicate';

export interface SeoHint {
  code: SeoHintCode;
  path?: string;
  field?: string;
  language?: string;
}

export type PathErrorCode =
  | 'path-format'
  | 'path-pattern'
  | 'path-duplicate'
  | 'path-site-page'
  | 'path-dynamic-wildcard'
  | 'path-root-wildcard'
  | 'path-no-page';

export const TITLE_MAX = 60;
export const DESCRIPTION_MIN = 120;
export const DESCRIPTION_MAX = 158;
export const LLMS_SUMMARY_MAX = 300;
export const LLMS_FACTS_MAX = 1500;
export const PLACEHOLDER_RE = /\[[^\]\n]{1,80}\]/;

const SHARE_IMAGE_MIN_WIDTH = 1200;
const SHARE_IMAGE_MIN_HEIGHT = 630;

/** Markup the server refuses in page meta and llms texts; quotes are fine (made typographic at publish). */
const META_MARKUP_RE = /[<>]/;

/** As the server's `validURL`: https with a host, none of `<>"'` and no whitespace. */
export const validImageUrl = (value: string): boolean => {
  if (/[<>"'\s]/.test(value) || !/^https:\/\/[^/]/i.test(value)) return false; // `new URL` reads `https:///x` as host x
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.host !== '';
  } catch {
    return false;
  }
};

/** A `*` anywhere but as one trailing `/*`. */
const misplacedStar = (path: string): boolean => {
  const stars = path.split('*').length - 1;
  return stars > 1 || (stars === 1 && !path.endsWith('/*'));
};

/**
 * Why an owner may not add `path`, or null. Checked in this order so the most telling reason wins: a `set_pages`
 * url is reported as a site page, not as a duplicate of its own row.
 */
export const validatePath = (
  path: string,
  existing: readonly string[],
  pages: readonly SiteSeoPage[],
): PathErrorCode | null => {
  if (path === '/*' || path === '*') return 'path-root-wildcard';
  if (!path.startsWith('/') || path.includes('?') || path.includes('#') || misplacedStar(path)) return 'path-format';
  if (isPatternPath(path)) return 'path-pattern';
  if (pages.some((p) => p.path === path)) return 'path-site-page';
  if (pages.some((p) => p.match === 'pattern' && derivedWildcard(p.path) === path)) return 'path-dynamic-wildcard';
  if (existing.includes(path)) return 'path-duplicate';
  return null;
};

/**
 * The prefix a footer section covers (`/casino/` for `/casino/*`), which the Add route dialog asks the server about
 * (some page of the site must start with it); null for anything but one trailing `/*` under a prefix.
 */
export const validateWildcardPrefix = (path: string): string | null => {
  if (!path.startsWith('/') || path === '/*' || !path.endsWith('/*') || path.split('*').length !== 2) return null;
  return path.slice(0, -1);
};

/** '' is fine; otherwise an absolute https url on the site's own host. */
export const validateCanonical = (value: string, siteHost: string): 'canonical-invalid' | null => {
  if (value === '') return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.host.toLowerCase() === siteHost.toLowerCase() ? null : 'canonical-invalid';
  } catch {
    return 'canonical-invalid';
  }
};

type TextField = 'title' | 'description' | 'footer_text' | 'title_template';

/** The hub's hints over the whole document (site first, then routes in document order). Never blocks Publish. */
export const validateDocument = (
  doc: SiteSeoDocument,
  ctx: { defaultLanguage: string; siteHost: string; pages: readonly SiteSeoPage[] },
): SeoHint[] => {
  const hints: SeoHint[] = [];
  const at = (path: string | undefined): Pick<SeoHint, 'path'> => (path === undefined ? {} : { path });

  const checkText = (path: string | undefined, field: TextField, map: Localized<string> | undefined): void => {
    if (!map) return;
    const values = Object.entries(map).filter(([, v]) => v !== '');
    if (!values.length) return;
    for (const [language, value] of values) {
      const hint = { ...at(path), field, language };
      if (field === 'title' && value.length > TITLE_MAX) hints.push({ code: 'title-long', ...hint });
      if (field === 'description' && value.length < DESCRIPTION_MIN) hints.push({ code: 'description-short', ...hint });
      if (field === 'description' && value.length > DESCRIPTION_MAX) hints.push({ code: 'description-long', ...hint });
      if (field === 'title_template' && !templateHasPageToken(value)) hints.push({ code: 'template-no-page', ...hint });
      if (PLACEHOLDER_RE.test(value)) hints.push({ code: 'placeholder', ...hint });
      if (field !== 'footer_text' && META_MARKUP_RE.test(value)) hints.push({ code: 'meta-markup', ...hint });
    }
    if (!map[ctx.defaultLanguage])
      hints.push({ code: 'default-language-missing', ...at(path), field, language: ctx.defaultLanguage });
  };

  const checkImage = (path: string | undefined, image: ImageRef | undefined): void => {
    if (image && image.url !== '' && !validImageUrl(image.url)) {
      hints.push({ code: 'image-url-invalid', ...at(path), field: 'share_image' });
      return;
    }
    if (!image || image.width <= 0 || image.height <= 0) return; // unknown dimensions: nothing to say
    if (image.width < SHARE_IMAGE_MIN_WIDTH || image.height < SHARE_IMAGE_MIN_HEIGHT) {
      hints.push({ code: 'image-small', ...at(path), field: 'share_image' });
    }
  };

  const { site } = doc;
  checkText(undefined, 'title_template', site.title_template);
  checkText(undefined, 'description', site.description);
  checkText(undefined, 'footer_text', site.footer_text);
  checkImage(undefined, site.share_image);
  const { llms_summary: summary = '', llms_facts: facts = '' } = site.ai;
  if (summary.length > LLMS_SUMMARY_MAX) hints.push({ code: 'llms-summary-long', field: 'llms_summary' });
  if (facts.length > LLMS_FACTS_MAX) hints.push({ code: 'llms-facts-long', field: 'llms_facts' });
  if (PLACEHOLDER_RE.test(summary)) hints.push({ code: 'placeholder', field: 'llms_summary' });
  if (PLACEHOLDER_RE.test(facts)) hints.push({ code: 'placeholder', field: 'llms_facts' });
  if (META_MARKUP_RE.test(summary)) hints.push({ code: 'meta-markup', field: 'llms_summary' });
  if (META_MARKUP_RE.test(facts)) hints.push({ code: 'meta-markup', field: 'llms_facts' });

  const hasFooterText = (route: SiteSeoDocument['routes'][number]): boolean =>
    Object.values(route.footer_text ?? {}).some((v) => v !== '');
  const ruleCounts = new Map<string, number>();
  const ruleTexts = new Map<string, number>(); // routes with footer text per rule path
  for (const route of doc.routes) {
    const rulePath = footerRulePathOf(route);
    if (rulePath === null) continue;
    ruleCounts.set(rulePath, (ruleCounts.get(rulePath) ?? 0) + 1);
    if (hasFooterText(route)) ruleTexts.set(rulePath, (ruleTexts.get(rulePath) ?? 0) + 1);
  }
  const firstTextOf = new Map<string, string>(); // rule path -> the first route with footer text under it

  for (const route of doc.routes) {
    const { path } = route;
    checkText(path, 'title', route.title);
    checkText(path, 'description', route.description);
    checkText(path, 'footer_text', route.footer_text);
    checkImage(path, route.share_image);
    if (route.canonical && validateCanonical(route.canonical, ctx.siteHost)) {
      hints.push({ code: 'canonical-invalid', path, field: 'canonical' });
    }
    const nameSupported = route.match === 'pattern' && ctx.pages.some((p) => p.path === path && p.expanded);
    if (!nameSupported) {
      for (const field of ['title', 'description'] as const) {
        for (const [language, value] of Object.entries(route[field] ?? {})) {
          if (value.includes(NAME_TOKEN)) hints.push({ code: 'name-token-unsupported', path, field, language });
        }
      }
    }
    const rulePath = footerRulePathOf(route);
    if (rulePath === null) continue;
    // Two footer texts under one rule path: the server's error, on the later route. Otherwise a shared rule path is
    // the hub's own collision hint (the site's winner is undefined).
    if ((ruleTexts.get(rulePath) ?? 0) > 1) {
      if (hasFooterText(route)) {
        const first = firstTextOf.get(rulePath);
        if (first === undefined) firstTextOf.set(rulePath, path);
        else if (first !== path) hints.push({ code: 'footer-path-duplicate', path, field: 'footer_text' });
      }
    } else if ((ruleCounts.get(rulePath) ?? 0) > 1) {
      hints.push({ code: 'wildcard-collision', path, field: 'footer_text' });
    }
  }
  return hints;
};

/** Publish is disabled only by a server issue of level `error` (Decision 13). */
export const blocksPublish = (issues: readonly SiteSeoIssue[]): boolean => issues.some((i) => i.level === 'error');
